import type { Game } from '@/types/reveal';
import type { PropCardMeta, DayFixture } from '@/types/reveal';
import { gradeNflProp, type NFLPropOperator } from '@low6-pickem/sports-config';

/** True when this contest resolves to prop-based Daily Reveal gameplay. */
export function isPropBasedGame(
  game: Pick<Game, 'mode' | 'dailyRevealMode' | 'sport'> | null | undefined
): boolean {
  return game?.mode === 'prop_based';
}

const PROP_STAT_LABELS: Record<string, string> = {
  receptions: 'Receptions',
  rushing_yards: 'Rushing Yards',
  passing_yards: 'Passing Yards',
  receiving_yards: 'Receiving Yards',
  anytime_touchdown: 'Anytime TD',
  sacks: 'Sacks',
  passing_touchdowns: 'Passing TDs',
  all_purpose_yards: 'All-Purpose Yards',
  hitting_h: 'Hits',
  hitting_runs: 'Runs',
  hitting_rbi: 'RBIs',
  hitting_hr: 'Home Runs',
  hitting_bb: 'Walks',
  hitting_sb: 'Steals',
  pitching_k: 'Strikeouts',
  points: 'Points',
  three_points_made: 'Three Pointers',
  rebounds: 'Rebounds',
  assists: 'Assists',
  steals: 'Steals',
  blocks: 'Blocks',
  soccer_goals: 'Goals',
  soccer_assists: 'Assists',
  soccer_shots: 'Shots',
  soccer_shots_on_target: 'Shots On Target',
};

/** Compact tracker / pick-line abbreviations (graded + live prop cards). */
const PROP_STAT_ABBREV: Record<string, string> = {
  receptions: 'REC',
  receiving_yards: 'REC YDS',
  rushing_yards: 'RUSH YDS',
  anytime_touchdown: 'ATD',
  passing_yards: 'PASS YDS',
  passing_touchdowns: 'PASS TD',
  all_purpose_yards: 'ALL YDS',
  sacks: 'SACKS',
  hitting_h: 'HITS',
  hitting_runs: 'RUNS',
  hitting_rbi: 'RBIS',
  hitting_hr: 'HR',
  hitting_bb: 'WALKS',
  hitting_sb: 'SB',
  pitching_k: 'K',
  points: 'POINTS',
  three_points_made: 'THREE POINTERS',
  rebounds: 'REBOUNDS',
  assists: 'ASSISTS',
  steals: 'STEALS',
  blocks: 'BLOCKS',
  soccer_goals: 'GOALS',
  soccer_assists: 'ASSISTS',
  soccer_shots: 'SHOTS',
  soccer_shots_on_target: 'SHOTS ON TARGET',
};

function isBaseballSport(sport: string | null | undefined): boolean {
  const normalized = sport?.trim().toUpperCase();
  return normalized === 'MLB' || normalized === 'BASEBALL';
}

/** Match frozen display-label phrases (e.g. "Over 3.5 Receptions") to the same abbrevs. */
const PROP_LABEL_ABBREV_PATTERNS: Array<{ pattern: RegExp; abbrev: string }> = [
  { pattern: /\ball[- ]?purp(?:ose)?\s+y(?:ar)?ds?\b/i, abbrev: 'ALL YDS' },
  { pattern: /\brec(?:eiving)?\s+y(?:ar)?ds?\b/i, abbrev: 'REC YDS' },
  { pattern: /\brush(?:ing)?\s+y(?:ar)?ds?\b/i, abbrev: 'RUSH YDS' },
  { pattern: /\bpass(?:ing)?\s+y(?:ar)?ds?\b/i, abbrev: 'PASS YDS' },
  { pattern: /\bpassing\s+touchdowns?\b/i, abbrev: 'PASS TD' },
  { pattern: /\bpass(?:ing)?\s+tds?\b/i, abbrev: 'PASS TD' },
  { pattern: /\banytime\s+(?:touchdowns?|td)\b/i, abbrev: 'ATD' },
  { pattern: /\bscore\s+a\s+td\b/i, abbrev: 'ATD' },
  { pattern: /\breceptions?\b/i, abbrev: 'REC' },
  { pattern: /\bsacks?\b/i, abbrev: 'SACKS' },
  { pattern: /\bhome\s+runs?\b/i, abbrev: 'HR' },
  { pattern: /\b(?:pitcher\s+)?strikeouts?\b/i, abbrev: 'K' },
  { pattern: /\bthree\s+point(?:er)?s?(?:\s+made)?\b/i, abbrev: 'THREE POINTERS' },
  { pattern: /\bshots?\s+on\s+target\b/i, abbrev: 'SHOTS ON TARGET' },
];

/**
 * A frozen label carries the stat's display NAME, not its stat type, and one name
 * covers two different markets: an MLB "Steals" is a stolen base (SB), an NBA one is
 * a defensive steal. Every caller knows the card's sport, so it decides that case.
 */
function abbreviatePropStatPhrase(
  phrase: string | null | undefined,
  sport?: string | null
): string | null {
  if (!phrase) return null;
  const trimmed = phrase.trim();
  if (!trimmed) return null;
  if (/^steals?$/i.test(trimmed) && isBaseballSport(sport)) return 'SB';
  for (const { pattern, abbrev } of PROP_LABEL_ABBREV_PATTERNS) {
    if (pattern.test(trimmed)) return abbrev;
  }
  // Already-compact tokens like "REC" / "ATD" — keep as-is (uppercase for consistency).
  if (/^[A-Z0-9]+(?:\s+[A-Z0-9]+)*$/i.test(trimmed) && trimmed.length <= 12) {
    return trimmed.toUpperCase();
  }
  return trimmed.toUpperCase();
}

/**
 * Parse frozen labels ("Over 3.5 Receptions", "39.5+ Receiving Yards", "Score a TD")
 * into the compact tracker parts: value like "3.5+" and abbrev like "REC".
 */
export function parseFrozenPropDisplayLabel(
  label: string | null | undefined,
  sport?: string | null
): { value: string; label: string } | null {
  if (!label) return null;
  const trimmed = label.trim();
  if (!trimmed) return null;

  if (/^score\s+a\s+td$/i.test(trimmed) || /^anytime\s+(?:touchdowns?|td)$/i.test(trimmed)) {
    return { value: '1+', label: 'ATD' };
  }

  const plusMatch = trimmed.match(/^([0-9]+(?:\.[0-9]+)?)\+\s+(.+)$/i);
  if (plusMatch) {
    const abbrev = abbreviatePropStatPhrase(plusMatch[2], sport);
    if (!abbrev) return null;
    return { value: `${plusMatch[1]}+`, label: abbrev };
  }

  const overMatch = trimmed.match(/^over\s+([0-9]+(?:\.[0-9]+)?)\s+(.+)$/i);
  if (overMatch) {
    const abbrev = abbreviatePropStatPhrase(overMatch[2], sport);
    if (!abbrev) return null;
    return {
      value: `${formatPropThreshold(Number(overMatch[1]))}+`,
      label: abbrev,
    };
  }

  const abbrevOnly = abbreviatePropStatPhrase(trimmed, sport);
  if (abbrevOnly && abbrevOnly !== trimmed.toUpperCase()) {
    return { value: '', label: abbrevOnly };
  }

  return null;
}

export type PropCardDisplayParts = {
  actionLine: string;
  statLine: string;
  statAbbrev: string;
};

function formatPropThreshold(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
}

export { formatPropThreshold };

/** Split a frozen prop into headline lines for card UI. */
export function getPropCardDisplayParts(
  prop: Pick<PropCardMeta, 'threshold' | 'operator' | 'statType'> | null | undefined
): PropCardDisplayParts | null {
  if (!prop) return null;
  // A pick rebuilt from a stored entry (every day but the one just played) carries the
  // frozen label, threshold and operator but NO stat type — the entry row does not store
  // one. Without this guard those props produced a parts object whose stat name was the
  // empty string, and the card printed a bare "33.5+" with nothing saying what the market
  // was. Returning null sends the caller to the frozen label, which names the stat.
  if (!prop.statType?.trim()) return null;

  const statLine = (
    PROP_STAT_LABELS[prop.statType] ?? prop.statType.replace(/_/g, ' ')
  ).toUpperCase();
  const statAbbrev = (PROP_STAT_ABBREV[prop.statType] ?? statLine).toUpperCase();

  // Anytime TD is a binary "did they score" market, so the stored line — 0.5 on
  // some feeds, 1 on others — is a threshold, not a number the player has to beat.
  // Deriving the headline from it printed "OVER 1 ANYTIME TD", which asks for two
  // touchdowns and is simply not what wins the card. Always state the real bar.
  if (prop.statType === 'anytime_touchdown') {
    return { actionLine: '1+', statLine, statAbbrev };
  }

  switch (prop.operator) {
    case 'over_or_true':
      // A 0.5 line on a counted stat (home run, steal, goal) means "record one" —
      // and it is not always a touchdown, so it must not borrow the TD wording.
      if (prop.threshold <= 0.5) {
        return { actionLine: '1+', statLine, statAbbrev };
      }
      return { actionLine: `OVER ${formatPropThreshold(prop.threshold)}`, statLine, statAbbrev };
    case 'over_or_equal':
      return { actionLine: `${formatPropThreshold(prop.threshold)}+`, statLine, statAbbrev };
    case 'over':
    default:
      return { actionLine: `OVER ${formatPropThreshold(prop.threshold)}`, statLine, statAbbrev };
  }
}

/**
 * Single prop headline for select cards — no American odds.
 * e.g. "OVER 86.5 RUSH YDS", "SCORE ANYTIME TD", "1+ SACKS"
 */
export function getPropCardHeadline(
  prop: Pick<PropCardMeta, 'threshold' | 'operator' | 'statType'> | null | undefined
): string | null {
  const parts = getPropCardDisplayParts(prop);
  if (!parts) return null;
  if (parts.actionLine === parts.statAbbrev) return parts.actionLine;
  return `${parts.actionLine} ${parts.statAbbrev}`;
}

/** Today's Pick / tracker row: `{n}+` value + compact stat abbrev (no "OVER"). */
export function getPropPickSummary(
  prop: Pick<PropCardMeta, 'threshold' | 'operator' | 'statType'> | null | undefined
): { value: string; label: string } | null {
  if (!prop) return null;
  const parts = getPropCardDisplayParts(prop);
  if (!parts) return null;

  const label = parts.statAbbrev;

  // Same bar as the card headline: an anytime TD (or any 0.5 binary line) is won
  // with one, whatever number the feed stored on the market.
  if (prop.statType === 'anytime_touchdown' || prop.threshold <= 0.5) {
    return { value: '1+', label };
  }
  if (
    prop.operator === 'over' ||
    prop.operator === 'over_or_equal' ||
    prop.operator === 'over_or_true'
  ) {
    return { value: `${formatPropThreshold(prop.threshold)}+`, label };
  }
  return { value: parts.actionLine, label };
}

/**
 * Compact tracker line parts for graded/live prop cards.
 * Prefers structured prop fields; falls back to parsing frozen "Over n Stat" labels
 * so we never render "3.5+ OVER 3.5 RECEPTIONS".
 */
export function getPropTrackerDisplayParts(opts: {
  prop?: Pick<PropCardMeta, 'threshold' | 'operator' | 'statType'> | null;
  propDisplayLabel?: string | null;
  propThreshold?: number | null;
  propOperator?: string | null;
  /** Card's league — disambiguates stat names two sports share (see abbreviatePropStatPhrase). */
  sport?: string | null;
}): { value: string; label: string } | null {
  const fromProp = getPropPickSummary(opts.prop ?? null);
  // Only the structured prop can be trusted when it actually names its stat; otherwise
  // the frozen label below is the one thing that does.
  if (fromProp?.label) return fromProp;

  const parsed = parseFrozenPropDisplayLabel(opts.propDisplayLabel, opts.sport);
  const threshold =
    opts.propThreshold != null && Number.isFinite(Number(opts.propThreshold))
      ? Number(opts.propThreshold)
      : null;

  if (threshold != null && parsed?.label) {
    if (parsed.label === 'ATD' || threshold <= 0.5) {
      return { value: '1+', label: parsed.label };
    }
    return { value: `${formatPropThreshold(threshold)}+`, label: parsed.label };
  }

  if (parsed?.value && parsed.label) return parsed;

  if (threshold != null) {
    const abbrev = abbreviatePropStatPhrase(opts.propDisplayLabel, opts.sport);
    if (abbrev) {
      if (abbrev === 'ATD' || threshold <= 0.5) {
        return { value: '1+', label: abbrev };
      }
      return { value: `${formatPropThreshold(threshold)}+`, label: abbrev };
    }
  }

  return parsed?.label ? { value: parsed.value || '', label: parsed.label } : null;
}

/** Format lock deadline in ET for display. */
export function formatPropLockTime(lockAtIso: string | null | undefined): string | null {
  if (!lockAtIso) return null;
  try {
    return new Date(lockAtIso).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return null;
  }
}

export type PropTrackerOutcome = 'pending' | 'hit' | 'miss' | 'void';

/** Progress-bar outcome from graded status, live feed value, or compatibility totalHits. */
export function resolvePropTrackerOutcome(opts: {
  propResultStatus?: string | null;
  propResultValue?: number | null;
  threshold?: number | null;
  operator?: string | null;
  totalHits?: number | null;
  isGraded?: boolean;
  isLiveOrFinished?: boolean;
}): PropTrackerOutcome {
  const status = opts.propResultStatus;
  if (status === 'won' || status === 'credited') return 'hit';
  if (status === 'lost') return 'miss';
  if (status === 'void') return 'void';

  const threshold = opts.threshold != null ? Number(opts.threshold) : null;
  const canScore =
    threshold != null && Number.isFinite(threshold) && (opts.isLiveOrFinished || opts.isGraded);

  if (canScore) {
    // Null/absent feed value during live or final means 0 so far — still show hit/miss chrome.
    const value = Number(opts.propResultValue ?? 0);
    const operator = (opts.operator ?? 'over') as NFLPropOperator;
    const outcome = gradeNflProp(value, threshold, operator);
    return outcome === 'won' ? 'hit' : 'miss';
  }

  if (opts.isGraded && opts.totalHits != null) {
    return opts.totalHits > 0 ? 'hit' : 'miss';
  }

  return 'pending';
}

/** Figma card border: green win, red loss, neutral otherwise. */
export function getPropTrackerBorderClass(outcome: PropTrackerOutcome): string {
  switch (outcome) {
    case 'hit':
      return 'border-2 border-[#53D337]';
    case 'miss':
      return 'border-2 border-[#E9344A]';
    case 'void':
      return 'border-2 border-[#3a3a3a]';
    default:
      return 'border-2 border-[#3a3a3a]';
  }
}

/** Line for tracker row: "4.5+ REC" from pick summary / frozen display label. */
export function getPropTrackerLineLabel(
  prop: Pick<PropCardMeta, 'threshold' | 'operator' | 'statType'> | null | undefined,
  propDisplayLabel?: string | null,
  propThreshold?: number | null
): string | null {
  const parts = getPropTrackerDisplayParts({
    prop,
    propDisplayLabel,
    propThreshold,
  });
  if (parts?.value && parts.label) return `${parts.value} ${parts.label}`;
  if (parts?.label) return parts.label;
  return propDisplayLabel ?? null;
}

/** Parse "2+ HITS" / "4.5+ RECEPTIONS" frozen labels when entry threshold is absent. */
export function parsePropThresholdFromDisplayLabel(
  label: string | null | undefined
): number | null {
  if (!label) return null;
  const match = label.trim().match(/^([0-9]+(?:\.[0-9]+)?)\+/);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

/** Best available frozen threshold for the progress tracker. */
export function resolvePropTrackerThreshold(opts: {
  propThreshold?: number | null;
  propDisplayLabel?: string | null;
  prop?: Pick<PropCardMeta, 'threshold'> | null | undefined;
}): number | null {
  if (opts.propThreshold != null && Number.isFinite(Number(opts.propThreshold))) {
    return Number(opts.propThreshold);
  }
  if (opts.prop?.threshold != null && Number.isFinite(Number(opts.prop.threshold))) {
    return Number(opts.prop.threshold);
  }
  return parsePropThresholdFromDisplayLabel(opts.propDisplayLabel);
}

/** Infer over_or_equal from "2+ HITS" style labels when operator isn't on the entry. */
export function resolvePropTrackerOperator(opts: {
  propOperator?: string | null;
  propDisplayLabel?: string | null;
  prop?: Pick<PropCardMeta, 'operator'> | null | undefined;
}): NFLPropOperator {
  if (opts.propOperator) return opts.propOperator as NFLPropOperator;
  if (opts.prop?.operator) return opts.prop.operator;
  if (opts.propDisplayLabel && /\+/.test(opts.propDisplayLabel)) return 'over_or_equal';
  return 'over';
}

/** Whether a prop pick is locked (market lock time passed or fixture started). */
export function isPropPickLocked(
  prop: PropCardMeta | null | undefined,
  nowMs: number,
  fixture?: DayFixture | null
): boolean {
  if (prop?.lockAt) {
    const lockMs = new Date(prop.lockAt).getTime();
    if (Number.isFinite(lockMs) && nowMs >= lockMs) return true;
  }
  if (fixture) {
    if (['live', 'finished', 'cancelled'].includes(fixture.status)) return true;
    if (fixture.startTime) {
      return nowMs >= new Date(fixture.startTime).getTime() - 60_000;
    }
  }
  return false;
}

export function formatPropResultLabel(
  propResultStatus: string | null | undefined,
  propResultValue: number | null | undefined,
  propDisplayLabel: string | null | undefined,
  pendingLabel?: string
): { label: string; won: boolean; showIcon: boolean } {
  const pending = pendingLabel ?? propDisplayLabel ?? 'Pending';

  switch (propResultStatus) {
    case 'won':
      return {
        label:
          propResultValue != null && propDisplayLabel
            ? `Won · ${propResultValue} (${propDisplayLabel})`
            : propResultValue != null
              ? `Won · ${propResultValue}`
              : 'Won',
        won: true,
        showIcon: true,
      };
    case 'credited':
      return { label: 'Credited Win', won: true, showIcon: true };
    case 'void':
      return { label: 'Void', won: false, showIcon: true };
    case 'lost':
      return {
        label: propResultValue != null ? `Lost · ${propResultValue}` : 'Lost',
        won: false,
        showIcon: true,
      };
    case 'pending':
    default:
      return { label: pending, won: false, showIcon: false };
  }
}
